/**
 * Product category hierarchy for Wonder Herbals.
 * Covers Skin, Hair, Wellness, Nutrition, Herbs, and Ayurveda categories
 * with subcategories for filtering and navigation.
 */

export interface Category {
  id: string;
  slug: string;
  name: string;
  description: string;
  icon: string;
  subcategories: Subcategory[];
}

export interface Subcategory {
  id: string;
  slug: string;
  name: string;
}

export const CATEGORIES: Category[] = [
  {
    id: 'cat_skin',
    slug: 'skin',
    name: 'Skin Care',
    description: 'Ayurvedic formulations for radiant, healthy skin',
    icon: '✨',
    subcategories: [
      { id: 'sub_face_oils', slug: 'face-oils', name: 'Face Oils' },
      { id: 'sub_face_packs', slug: 'face-packs', name: 'Face Packs & Ubtan' },
      { id: 'sub_serums', slug: 'serums', name: 'Serums' },
      { id: 'sub_cleansers', slug: 'cleansers', name: 'Cleansers' },
      { id: 'sub_moisturizers', slug: 'moisturizers', name: 'Moisturizers' },
      { id: 'sub_sunscreen', slug: 'sunscreen', name: 'Sun Protection' },
      { id: 'sub_lip_care', slug: 'lip-care', name: 'Lip Care' },
      { id: 'sub_body_oils', slug: 'body-oils', name: 'Body Oils' },
    ],
  },
  {
    id: 'cat_hair',
    slug: 'hair',
    name: 'Hair Care',
    description: 'Traditional Ayurvedic hair oils and treatments',
    icon: '💇',
    subcategories: [
      { id: 'sub_hair_oils', slug: 'hair-oils', name: 'Hair Oils' },
      { id: 'sub_hair_masks', slug: 'hair-masks', name: 'Hair Masks' },
      { id: 'sub_shampoo', slug: 'shampoo', name: 'Herbal Shampoo' },
      { id: 'sub_conditioner', slug: 'conditioner', name: 'Conditioners' },
      { id: 'sub_scalp_treatment', slug: 'scalp-treatment', name: 'Scalp Treatments' },
      { id: 'sub_hair_color', slug: 'hair-color', name: 'Natural Hair Color' },
    ],
  },
  {
    id: 'cat_wellness',
    slug: 'wellness',
    name: 'Wellness',
    description: 'Holistic Ayurvedic wellness for mind, body, and spirit',
    icon: '🧘',
    subcategories: [
      { id: 'sub_immunity', slug: 'immunity', name: 'Immunity Boosters' },
      { id: 'sub_digestive', slug: 'digestive', name: 'Digestive Health' },
      { id: 'sub_stress', slug: 'stress-relief', name: 'Stress & Sleep' },
      { id: 'sub_joint_pain', slug: 'joint-pain', name: 'Joint & Muscle Care' },
      { id: 'sub_respiratory', slug: 'respiratory', name: 'Respiratory Health' },
      { id: 'sub_womens', slug: 'womens-health', name: "Women's Health" },
      { id: 'sub_mens', slug: 'mens-health', name: "Men's Health" },
      { id: 'sub_mental', slug: 'mental-wellness', name: 'Mental Wellness' },
    ],
  },
  {
    id: 'cat_nutrition',
    slug: 'nutrition',
    name: 'Nutrition',
    description: 'Ayurvedic superfoods and nutritional supplements',
    icon: '🥗',
    subcategories: [
      { id: 'sub_chyawanprash', slug: 'chyawanprash', name: 'Chyawanprash' },
      { id: 'sub_health_tonic', slug: 'health-tonics', name: 'Health Tonics' },
      { id: 'sub_superfood', slug: 'superfoods', name: 'Superfoods' },
      { id: 'sub_protein', slug: 'protein', name: 'Plant Protein' },
      { id: 'sub_vitamins', slug: 'vitamins', name: 'Ayurvedic Vitamins' },
    ],
  },
  {
    id: 'cat_herbs',
    slug: 'herbs',
    name: 'Raw Herbs',
    description: 'Pure, stone-crushed herbs sourced from pristine regions',
    icon: '🌿',
    subcategories: [
      { id: 'sub_single_herbs', slug: 'single-herbs', name: 'Single Herbs' },
      { id: 'sub_herb_powders', slug: 'herb-powders', name: 'Herb Powders (Churna)' },
      { id: 'sub_herb_pastes', slug: 'herb-pastes', name: 'Herb Pastes' },
      { id: 'sub_essential_oils', slug: 'essential-oils', name: 'Essential Oils' },
      { id: 'sub_resins', slug: 'resins', name: 'Resins & Gums' },
    ],
  },
  {
    id: 'cat_ayurveda',
    slug: 'ayurveda',
    name: 'Ayurvedic Formulations',
    description: 'Classical Ayurvedic preparations from ancient texts',
    icon: '🕉️',
    subcategories: [
      { id: 'sub_churna', slug: 'churna', name: 'Churna (Powders)' },
      { id: 'sub_taila', slug: 'taila', name: 'Taila (Medicated Oils)' },
      { id: 'sub_lehya', slug: 'lehya', name: 'Lehya (Confections)' },
      { id: 'sub_vati', slug: 'vati', name: 'Vati (Tablets)' },
      { id: 'sub_arishta', slug: 'arishta', name: 'Arishta (Fermented)' },
      { id: 'sub_kashaya', slug: 'kashaya', name: 'Kashaya (Decoctions)' },
      { id: 'sub_guggulu', slug: 'guggulu', name: 'Guggulu Preparations' },
      { id: 'sub_ras', slug: 'ras', name: 'Ras Rasayan' },
    ],
  },
];

/**
 * Lookup map: category slug → Category object
 */
export const CATEGORY_MAP = new Map(
  CATEGORIES.map((cat) => [cat.slug, cat])
);

/**
 * Lookup map: subcategory slug → Subcategory object
 */
export const SUBCATEGORY_MAP = new Map(
  CATEGORIES.flatMap((cat) =>
    cat.subcategories.map((sub) => [sub.slug, sub])
  )
);

/**
 * Get all category slugs for URL generation
 */
export const CATEGORY_SLUGS = CATEGORIES.map((cat) => cat.slug);

/**
 * Get all subcategory slugs
 */
export const SUBCATEGORY_SLUGS = CATEGORIES.flatMap((cat) =>
  cat.subcategories.map((sub) => sub.slug)
);
